import type { RefObject } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Box,
  HStack,
  VStack,
  Input as ChakraInput,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'
import type { SingleValue } from 'react-select'

import { useTranslation } from 'react-i18next'
import { IKReactSelect } from '../../../../ui-kit'
import type {
  InputFormProps as Props,
  DimensionsFilterValue,
} from '../../../interfaces'
import {
  getFieldsMapping,
  validateFilter,
} from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'

const DimensionsInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props
  const { t } = useTranslation()
  const [dirty, setDirty] = useState<boolean>(false)
  const [localValue, setLocalValue] = useState<{
    width: { min: string }
    height: { min: string }
  }>({
    width: { min: '' },
    height: { min: '' },
  })

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(() => {
    // If field is dirty (user is typing), validate against localValue
    // Only for operators that actually use localValue (custom dimensions input)
    if (dirty && ['CUSTOM_DIMENSIONS'].includes(expression.operator)) {
      // Create a temporary expression with localValue for validation
      const tempExpression = {
        ...expression,
        value: localValue,
      }

      // Validate the temporary expression with localValue
      const validation = validateFilter({
        expression: tempExpression,
        customMetadataField: undefined, // Dimensions field doesn't use custom metadata
      })

      return validation.error
    }

    // If not dirty or for operators that don't use localValue, use the normal validation from validationObject
    return validationObject.errors[groupId][filterId]
  }, [validationObject, groupId, filterId, dirty, localValue, expression])

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: DimensionsFilterValue) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  // Initialize local value from expression
  useEffect(() => {
    setLocalValue(
      (expression.value as {
        width: { min: string }
        height: { min: string }
      }) || {
        width: { min: '' },
        height: { min: '' },
      },
    )
  }, [expression.value])

  const handleLocalValueChange = useCallback(
    (newValue: { width: { min: string }; height: { min: string } }) => {
      setLocalValue(newValue)
      setDirty(true)
    },
    [],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleValueChange(localValue)
        setDirty(false)
        // Reset focus by blurring the input
        e.currentTarget.blur()
      }
    },
    [localValue, handleValueChange],
  )

  const handleBlur = useCallback(() => {
    handleValueChange(localValue)
    setDirty(false)
  }, [localValue, handleValueChange])

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em" alignItems="stretch">
        <OperatorMenu
          width="full"
          expression={expression}
          handleOperatorChange={handleOperatorChange}
        />

        {['IS'].includes(expression.operator) && (
          <Box width="full">
            <IKReactSelect
              menuPlacement="auto"
              ref={initialFocusRef}
              variant="compact"
              placeholder="Select a size"
              isSearchable={true}
              options={getFieldsMapping({
                fieldName: expression.fieldName,
              }).possibleValues?.map(val => ({
                label: val as string,
                value: val as string,
              }))}
              value={
                expression.value
                  ? {
                      label: expression.value as string,
                      value: expression.value as string,
                    }
                  : null
              }
              onChange={(
                newVal: SingleValue<{
                  label: string
                  value: string
                }>,
              ) => {
                newVal?.value && handleValueChange(newVal.value)
                setDirty(true)
              }}
            />
          </Box>
        )}

        {['CUSTOM_DIMENSIONS'].includes(expression.operator) && (
          <HStack width="full" spacing="0.5em">
            <ChakraInput
              ref={initialFocusRef as RefObject<HTMLInputElement>}
              placeholder={t('min_width', 'Min. Width')}
              name={expression.fieldName}
              value={localValue?.width?.min || ''}
              onChange={e => {
                const newValue = {
                  ...localValue,
                  width: { min: e.target.value },
                }
                handleLocalValueChange(newValue)
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              bgColor="white"
            />
            <ChakraInput
              placeholder={t('min_height', 'Min. Height')}
              name={expression.fieldName}
              value={localValue?.height?.min || ''}
              onChange={e => {
                const newValue = {
                  ...localValue,
                  height: { min: e.target.value },
                }
                handleLocalValueChange(newValue)
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              bgColor="white"
            />
          </HStack>
        )}

        <ChakraFormErrorMessage width="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default DimensionsInputForm
